if (!customElements.get('td-results-stats')) {
  class TDResultsStats extends HTMLElement {
    static SELECTORS = {
      counter: '[data-td-results-counter]',
      fill: '[data-td-results-fill]',
      card: '[data-td-results-card]',
      slider: '[data-td-results-stats-slider]',
      viewport: '[data-td-embla-viewport]',
    };

    constructor() {
      super();
      this.handleCounterIntersection = this.handleCounterIntersection.bind(this);
      this.handleSectionIntersection = this.handleSectionIntersection.bind(this);
      this.handleEmblaInit = this.handleEmblaInit.bind(this);
      this.handleResize = this.handleResize.bind(this);
    }

    connectedCallback() {
      this.reduceMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.counters = Array.from(this.querySelectorAll(TDResultsStats.SELECTORS.counter)).map((element) => ({
        element,
        target: Number(element.dataset.tdTarget || 0),
        unit: element.dataset.tdUnit || '',
        decimals: Number(element.dataset.tdDecimals || 0),
        animated: false,
        frameId: null,
      }));
      this.fills = Array.from(this.querySelectorAll(TDResultsStats.SELECTORS.fill)).map((element) => ({
        element,
        target: Number(element.dataset.tdTarget || 0),
        animated: false,
      }));

      this.sliderElement = this.querySelector(TDResultsStats.SELECTORS.slider);
      this.viewportElement = this.querySelector(TDResultsStats.SELECTORS.viewport);

      if (this.sliderElement instanceof HTMLElement) {
        this.sliderElement.addEventListener('embla:init', this.handleEmblaInit);
        window.addEventListener('resize', this.handleResize, { passive: true });

        if (this.sliderElement.emblaApi) {
          this.emblaApi = this.sliderElement.emblaApi;
        }

        this.updateSliderState();
      }

      if (!this.counters.length && !this.fills.length) {
        return;
      }

      if (this.reduceMotionQuery.matches || typeof IntersectionObserver !== 'function') {
        this.setFinalValues();
        return;
      }

      this.counterObserver = new IntersectionObserver(this.handleCounterIntersection, {
        threshold: 0.45,
      });
      this.counters.forEach((counter) => this.counterObserver.observe(counter.element));

      this.sectionObserver = new IntersectionObserver(this.handleSectionIntersection, {
        threshold: 0,
      });
      this.sectionObserver.observe(this);
    }

    disconnectedCallback() {
      this.counterObserver?.disconnect();
      this.sectionObserver?.disconnect();
      this.counterObserver = null;
      this.sectionObserver = null;

      this.sliderElement?.removeEventListener('embla:init', this.handleEmblaInit);
      window.removeEventListener('resize', this.handleResize);
      this.emblaApi = null;

      this.counters?.forEach((counter) => {
        if (counter.frameId) {
          cancelAnimationFrame(counter.frameId);
          counter.frameId = null;
        }
      });
    }

    handleEmblaInit(event) {
      this.emblaApi = event?.detail?.api || this.sliderElement?.emblaApi || null;
      this.updateSliderState();
    }

    handleResize() {
      this.emblaApi?.reInit();
      this.updateSliderState();
    }

    updateSliderState() {
      if (!(this.viewportElement instanceof HTMLElement)) {
        return;
      }

      const hasOverflow = this.viewportElement.scrollWidth - this.viewportElement.clientWidth > 1;
      this.dataset.tdSliderOverflow = hasOverflow ? 'true' : 'false';
    }

    handleCounterIntersection(entries) {
      entries.forEach((entry) => {
        if (!entry.isIntersecting || entry.intersectionRatio < 0.45) {
          return;
        }

        const counter = this.counters.find((item) => item.element === entry.target);
        if (!counter || counter.animated) {
          return;
        }

        counter.animated = true;
        this.counterObserver?.unobserve(counter.element);
        this.animateCounter(counter);

        const fill = this.fills.find(
          (item) => item.element.closest(TDResultsStats.SELECTORS.card) === counter.element.closest(TDResultsStats.SELECTORS.card)
        );
        if (fill && !fill.animated) {
          fill.animated = true;
          fill.element.style.setProperty('--td-results-stats-fill', `${fill.target}%`);
        }
      });
    }

    handleSectionIntersection(entries) {
      const entry = entries[entries.length - 1];
      if (!entry?.isIntersecting) {
        this.resetValues();
      }
    }

    animateCounter(counter) {
      const duration = 1100;
      const startTime = performance.now();

      const step = (timestamp) => {
        if (!this.isConnected) {
          return;
        }

        const progress = Math.min((timestamp - startTime) / duration, 1);
        const eased = 1 - Math.pow(1 - progress, 3);
        counter.element.textContent = `${this.formatValue(counter.target * eased, counter.decimals)}${counter.unit}`;

        if (progress < 1) {
          counter.frameId = requestAnimationFrame(step);
        } else {
          counter.element.textContent = `${this.formatValue(counter.target, counter.decimals)}${counter.unit}`;
          counter.frameId = null;
        }
      };

      counter.frameId = requestAnimationFrame(step);
    }

    resetValues() {
      this.counters.forEach((counter) => {
        if (counter.frameId) {
          cancelAnimationFrame(counter.frameId);
          counter.frameId = null;
        }

        counter.animated = false;
        counter.element.textContent = `0${counter.unit}`;
      });

      this.fills.forEach((fill) => {
        fill.animated = false;
        fill.element.style.setProperty('--td-results-stats-fill', '0%');
      });

      this.counters.forEach((counter) => this.counterObserver?.observe(counter.element));
    }

    setFinalValues() {
      this.counters.forEach((counter) => {
        counter.element.textContent = `${this.formatValue(counter.target, counter.decimals)}${counter.unit}`;
      });
      this.fills.forEach((fill) => {
        fill.element.style.setProperty('--td-results-stats-fill', `${fill.target}%`);
      });
    }

    formatValue(value, decimals) {
      return Number(value).toFixed(decimals).replace(/\.0+$/, '');
    }
  }

  customElements.define('td-results-stats', TDResultsStats);
}
